import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { CustomerLocationModalComponent } from '@ml/shared/components/customer-location-modal/customer-location-modal.component';
import { Location } from '@ml/locations/types';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { GoogleMapsService } from '@ml/shared/services/google-maps.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Task } from '@ml/tasks/types/classes';
import { UpdateTaskPosition } from '@ml/orders/types/update-task-position';
import { UpdateResourcePosition } from '@ml/shared/types/interfaces';
import { MediaObserver } from '@angular/flex-layout';

@Component({
  selector: 'ml-select-locations-table',
  template: `
    <table
      mat-table
      [dataSource]="locations"
      cdkDropList
      [cdkDropListData]="locations"
      [cdkDropListDisabled]="!canDrag"
      (cdkDropListDropped)="dropTable($event)"
      class="full-width"
    >
      <ng-container matColumnDef="index">
        <th mat-header-cell *matHeaderCellDef>
          {{ '#' | translate | titlecase }}
        </th>
        <td mat-cell *matCellDef="let element; let i = index">
          {{ i + 1 }}
        </td>
      </ng-container>

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'name' | translate | titlecase }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.name }}</td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'status' | translate | titlecase }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon
            svgIcon="map"
            [matTooltip]="element.status"
            [ngClass]="element.status"
          ></mat-icon>
        </td>
      </ng-container>

      <ng-container matColumnDef="preview">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'preview' | translate | titlecase }}
        </th>
        <td mat-cell *matCellDef="let element">
          <button mat-icon-button color="primary" (click)="onPreview(element)">
            <mat-icon svgIcon="view"></mat-icon>
          </button>
        </td>
      </ng-container>

      <ng-container matColumnDef="navigation">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'navigation' | translate | titlecase }}
        </th>
        <td mat-cell *matCellDef="let element">
          <button mat-icon-button (click)="onNavigation(element)">
            <mat-icon svgIcon="location"></mat-icon>
          </button>
        </td>
      </ng-container>

      <!-- Checkbox Column -->
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef>
          <mat-checkbox
            [disabled]="disableSelect"
            (change)="$event ? masterToggle() : null"
            [checked]="selection.hasValue() && isAllSelected()"
            [indeterminate]="selection.hasValue() && !isAllSelected()"
          >
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            [disabled]="disableSelect"
            (click)="$event.stopPropagation()"
            (change)="$event ? selection.toggle(row.id) : null"
            [checked]="selection.isSelected(row.id)"
          >
          </mat-checkbox>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        [ngClass]="{ 'cursor-pointer': canDrag }"
        style="width: 100%"
        *matRowDef="let row; columns: displayedColumns"
        cdkDrag
        cdkDragLockAxis="y"
        [cdkDragData]="row"
      ></tr>
    </table>

    <ml-empty-table
      [data]="locations"
      [loading]="loading"
      [message]="'no-locations'"
    ></ml-empty-table>
  `,
  styleUrls: ['./select-locations-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectLocationsTableComponent implements OnInit, AfterViewChecked {
  @Input() locations: Location[];
  @Input() selectedLocations: number[];
  @Input() loading: boolean;
  @Input() disableSelect: boolean;
  @Input() canDrag: boolean;

  @Output() addLocations = new EventEmitter<number[]>();
  @Output() removeLocations = new EventEmitter<number[]>();
  @Output() updatePositions = new EventEmitter<UpdateResourcePosition[]>();

  selection: SelectionModel<number>;

  displayedColumns = [
    'index',
    'name',
    'status',
    'preview',
    'navigation',
    'select'
  ];

  constructor(
    private dialog: MatDialog,
    private googleMapsService: GoogleMapsService,
    private cdRef: ChangeDetectorRef,
    private mediaObserver: MediaObserver
  ) {}

  ngOnInit() {
    this.selection = new SelectionModel(true, this.selectedLocations);

    this.selection.changed
      .asObservable()
      .pipe(distinctUntilChanged())
      .subscribe(value => {
        if (value.added.length > 0) {
          this.addLocations.emit(value.added);
        }
        if (value.removed.length > 0) {
          this.removeLocations.emit(value.removed);
        }
      });
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.locations.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      return this.selection.clear();
    }

    const ids = this.locations.map(value => value.id);
    this.selection.select(...ids);
  }

  onPreview(location: Location) {
    const isTablet = this.mediaObserver.isActive('lg');
    this.dialog.open(CustomerLocationModalComponent, {
      width: isTablet ? '800px' : '100vw',
      maxWidth: '100vw',
      data: {
        location: location,
        disable: true
      }
    });
  }

  onNavigation(location: Location) {
    this.googleMapsService.navigateTo(location);
  }

  dropTable(event: CdkDragDrop<Location[]>) {
    const prevIndex = this.locations.findIndex(
      d => d.id === event.item.data.id
    );
    const locations = [...this.locations];
    moveItemInArray(locations, prevIndex, event.currentIndex);
    this.locations = [...locations];

    const updatePositions: UpdateResourcePosition[] = this.locations.map(
      (location, index) => ({
        id: location.id,
        position: index
      })
    );
    this.updatePositions.emit(updatePositions);
  }
}
